import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Button, Snackbar, Box, LinearProgress } from '@mui/material';
import useVersionStore from '@/store/VersionStore';
import { performHardRefresh } from '@services/VersionService';

/** Long enough to notice and finish a sentence; short enough that nobody lingers. */
const AUTO_RELOAD_SECONDS = 10;

/**
 * "A new version is available" — with a countdown, then an automatic reload.
 *
 * Replaces a document.createElement + innerHTML banner that appended a fresh
 * <style> tag to <head> on every call and never removed the node.
 *
 * The countdown is the point. A hard refresh throws away anything unsaved — a
 * half-written daily plan, a form mid-edit — so the user gets a real chance to
 * finish or to reload deliberately. If a previous reload already failed to land
 * the new build (autoReloadBlocked), the countdown is abandoned entirely and this
 * becomes a manual button: auto-reloading again would just loop.
 */
const UpdateAvailableBanner: React.FC = () => {
  const updateAvailable = useVersionStore((s) => s.updateAvailable);
  const latestVersion = useVersionStore((s) => s.latestVersion);
  const autoReloadBlocked = useVersionStore((s) => s.autoReloadBlocked);

  const [secondsLeft, setSecondsLeft] = useState(AUTO_RELOAD_SECONDS);

  useEffect(() => {
    if (!updateAvailable || autoReloadBlocked) return undefined;

    if (secondsLeft <= 0) {
      performHardRefresh(latestVersion || undefined);
      return undefined;
    }

    const t = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [updateAvailable, autoReloadBlocked, secondsLeft, latestVersion]);

  if (!updateAvailable) return null;

  const reloadNow = () => performHardRefresh(latestVersion || undefined);

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      // Above the daily-ritual drawer (1500) and the app bar (1400) — an update
      // notice the blocking gate can cover is an update notice nobody sees.
      sx={{ zIndex: 1600, maxWidth: 420 }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ width: '100%', borderRadius: 2, boxShadow: 6 }}
        action={
          <Button color="inherit" size="small" onClick={reloadNow} sx={{ fontWeight: 700 }}>
            Reload now
          </Button>
        }
      >
        <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
          {latestVersion ? `Version ${latestVersion} is available` : 'A new version is available'}
        </AlertTitle>

        {autoReloadBlocked ? (
          "We couldn't load it automatically. Please reload to get the latest version."
        ) : (
          <>
            Reloading in {secondsLeft}s — finish up if you're mid-task.
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                color="inherit"
                value={(secondsLeft / AUTO_RELOAD_SECONDS) * 100}
                sx={{ height: 4, borderRadius: 2, opacity: 0.6 }}
              />
            </Box>
          </>
        )}
      </Alert>
    </Snackbar>
  );
};

export default UpdateAvailableBanner;
